// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from "react";
import {
  getOrCreateUserSchedulerSessionId,
  getUserSchedulerSessionId,
  heartbeatUserSchedulerSession,
  stopUserSchedulerSession,
} from "../../service/api/UserSchedulerPresenceApi";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Load from sessionStorage (auto cleared on tab close)
  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userInfo");
      const storedPlan = sessionStorage.getItem("userPlan");

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.accessToken) {
          setUser(parsedUser);
        } else {
          sessionStorage.removeItem("userInfo");
          sessionStorage.removeItem("userPlan");
        }
      }
      if (storedPlan) setUserPlan(JSON.parse(storedPlan));
    } catch (err) {
      console.error("Error parsing session data:", err);
      sessionStorage.clear();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const userId = user?.userId;
    const accessToken = user?.accessToken;
    if (!userId || !accessToken) return undefined;

    const sessionId = getOrCreateUserSchedulerSessionId();
    const heartbeat = () => {
      heartbeatUserSchedulerSession(accessToken, sessionId).catch((error) => {
        if (error?.status === 401) {
          setUser(null);
          setUserPlan(null);
          sessionStorage.clear();
          return;
        }
        console.warn("Unable to refresh user scheduler session:", error);
      });
    };

    heartbeat();
    const heartbeatTimer = window.setInterval(heartbeat, 30000);
    return () => window.clearInterval(heartbeatTimer);
  }, [user?.userId, user?.accessToken]);

  // ✅ Login
  const login = (userData, planData = null) => {
    try {
      setUser(userData);
      sessionStorage.setItem("userInfo", JSON.stringify(userData));

      if (planData) {
        setUserPlan(planData);
        sessionStorage.setItem("userPlan", JSON.stringify(planData));
      }
    } catch (error) {
      console.error("Login storage error:", error);
    }
  };

  // ✅ Update Plan
  const updateUserPlan = (planData) => {
    try {
      setUserPlan(planData);

      if (planData) {
        sessionStorage.setItem("userPlan", JSON.stringify(planData));
      } else {
        sessionStorage.removeItem("userPlan");
      }
    } catch (error) {
      console.error("Plan update error:", error);
    }
  };

  // ✅ Logout
  const logout = () => {
    try {
      const accessToken = user?.accessToken;
      const sessionId = getUserSchedulerSessionId();
      if (accessToken && sessionId) {
        stopUserSchedulerSession(accessToken, sessionId).catch((error) => {
          console.warn("Unable to stop user scheduler session:", error);
        });
      }
      setUser(null);
      setUserPlan(null);
      sessionStorage.clear(); // 🔥 important
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // ✅ Check auth
  const isAuthenticated = () => {
    return !!sessionStorage.getItem("userInfo");
  };

  // ✅ Safe getter
  const getUserPlan = () => {
    if (userPlan) return userPlan;

    try {
      const storedPlan = sessionStorage.getItem("userPlan");
      return storedPlan ? JSON.parse(storedPlan) : null;
    } catch {
      return null;
    }
  };


  const contextValue = {
    user,
    userPlan,
    setUserPlan: updateUserPlan,
    login,
    logout,
    getUserPlan,
    isAuthenticated,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => useContext(AuthContext);
